import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "./utils/cn";

type NativeProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "checked" | "onChange" | "type"
>;

export interface CheckboxProps extends NativeProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
}

/** Canonical checkbox + optional label. Supersedes common/Checkbox. */
const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ checked, onChange, label, disabled, id, className, ...rest }, ref) => {
    const reactId = useId();
    const inputId = id ?? reactId;

    return (
      <div
        className={cn(
          "flex min-w-0 items-center gap-3",
          disabled && "opacity-60"
        )}
      >
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className={cn(
            "h-[18px] w-[18px] shrink-0 cursor-pointer rounded border-border-strong text-secondary accent-secondary",
            "focus-visible:ring focus-visible:ring-offset-0",
            className
          )}
          {...rest}
        />
        {label && (
          <label
            htmlFor={inputId}
            className="flex min-w-0 cursor-pointer items-center gap-2 text-start font-normal text-menu"
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
